import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';

/**
 * Toast — canto inferior direito, `aria-live="polite"`, some em 4s
 * (UX-PAINEL-INTERNO §0.6). Só para confirmação de ação curta.
 */
interface ToastItem {
  id: number;
  texto: string;
  tom: 'ok' | 'erro';
}

type MostrarToast = (texto: string, tom?: ToastItem['tom']) => void;

const Ctx = createContext<MostrarToast | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [itens, setItens] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const mostrar = useCallback((texto: string, tom: ToastItem['tom'] = 'ok'): void => {
    const id = ++seq.current;
    setItens((atual) => [...atual, { id, texto, tom }]);
    window.setTimeout(() => {
      setItens((atual) => atual.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  return (
    <Ctx.Provider value={mostrar}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2"
      >
        {itens.map((t) => (
          <div
            key={t.id}
            className={
              'pointer-events-auto rounded-2 border px-3 py-2 text-corpo shadow-sm ' +
              (t.tom === 'erro'
                ? 'border-erro bg-aviso-fraco text-tinta-forte'
                : 'border-linha bg-superficie-alta text-tinta-forte')
            }
          >
            {t.texto}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): MostrarToast {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast fora de <ToastProvider>');
  return ctx;
}
